import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  brokerQuotes,
  operations,
  quoteFinancial,
  type Operation,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Icon } from '@/shared/ui/Icon';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { useToast } from '@/shared/ui/useToast';

import { type AcaoDoMenu } from '../../users/MenuDeAcoes';
import { nomeDoResumo, salvarArquivoDoFinanceiro } from '../financeiroDaCotacao';
import { PartyToolbar } from '../../shared/PartyToolbar';

import { BoletosDaCotacao } from './BoletosDaCotacao';
import { BrokerQuoteFiltersDialog } from './BrokerQuoteFiltersDialog';
import {
  filtrosDaCorretoraVazios,
  motivoSemMinuta,
  numeroDaCotacaoDaCorretora,
  PERMISSAO_DA_MINUTA_DA_CORRETORA,
  podeCancelarNaLista,
  quantosFiltrosDaCorretora,
  type BrokerQuoteFilters,
} from './cotacaoDaCorretora';
import { ListaDaCorretora } from './listaDaCorretora';
import { useImprimirMinuta } from './minutaDaCorretora';
import { QuoteTypeDialog } from './QuoteTypeDialog';
import { QuoteWithdrawDialog } from './QuoteWithdrawDialog';

/** Dez por pagina, como a lista de cotacoes da corretora do OnPoint (NCT-023). */
const limite = 10;

/**
 * Lista de cotacoes da corretora (`/propostas` do OnPoint para a CORRETORA; NCT-001 a
 * NCT-038). Mesma rota da lista da seguradora, com a visao da corretora (decisao 2 da
 * 11.11).
 *
 * Os dados da lista: Nro. da Cotacao, Tipo, Canal, Tomador, Segurado, Modalidade,
 * Status, Data da cotacao e IS, numa tabela que escolhe as colunas pela largura e nao
 * rola para o lado (listaDaCorretora.tsx, 04/11/2026). O menu da linha, na ultima
 * coluna, tem Visualizar cotacao, Cancelar cotacao (pela configuracao da empresa),
 * Imprimir cotacao, que baixa o resumo da cotacao do financeiro (P-COT-4, ligado pelo
 * P-NCT-L; NCT-019), Baixar boleto(s), com os boletos que a seguradora anexou as
 * parcelas (P-BOL; NCT-057), os da apolice depois da emissao (R5-A08), e Imprimir
 * minuta. A minuta e da corretora tambem desde a decisao do dono de 04/11/2026, que
 * resolveu NCT-020 e SUB-018 (ADR 0108): sai em PDF, com "SEM VALOR", depois da
 * aprovacao da seguradora; antes disso o item fica desligado, com o motivo. O servidor
 * so devolve as cotacoes da corretora da sessao.
 */
export function BrokerQuotesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [busca, setBusca] = useState('');
  const [offset, setOffset] = useState(0);
  const [filtros, setFiltros] = useState<BrokerQuoteFilters>(filtrosDaCorretoraVazios);
  const [filtrando, setFiltrando] = useState(false);
  const [escolhendo, setEscolhendo] = useState(false);
  const [cancelando, setCancelando] = useState<Operation | null>(null);
  const [boletosDe, setBoletosDe] = useState<Operation | null>(null);
  const aplicados = quantosFiltrosDaCorretora(filtros);
  const filtrado = busca !== '' || aplicados > 0;

  const podeCriar = can('operacoes.operacao.create');
  const podeCancelar = can('operacoes.cotacao.withdraw');
  const podeImprimirMinuta = can(PERMISSAO_DA_MINUTA_DA_CORRETORA);
  const imprimirMinuta = useImprimirMinuta();

  const consulta = useQuery({
    queryKey: ['operations', 'corretora', { busca, offset, filtros }],
    queryFn: ({ signal }) =>
      operations.list({ q: busca, limit: limite, offset, ...filtros }, signal),
  });
  const opcoes = useQuery({
    queryKey: ['broker-quote-options'],
    queryFn: ({ signal }) => brokerQuotes.options(signal),
    enabled: podeCancelar,
    retry: false,
  });
  const escopo = opcoes.data?.settings.broker_withdraw_scope;

  // "Imprimir cotacao": o resumo da cotacao (ObterResumoProposta), o mesmo do "Baixar
  // resumo" da linha do tempo. PDF quando a instalacao converte, Word quando nao.
  const imprimir = useMutation({
    mutationFn: (operacao: Operation) => quoteFinancial.summary(operacao.id),
    onSuccess: (arquivo, operacao) =>
      salvarArquivoDoFinanceiro(arquivo, nomeDoResumo(operacao, arquivo)),
    onError: (error: Error) => toast.error(error.message),
  });

  const numeroDe = numeroDaCotacaoDaCorretora;

  // O menu da linha, na ordem do menu da corretora: Visualizar cotacao, Cancelar
  // cotacao (pela configuracao da empresa), Imprimir cotacao, Baixar boleto(s) e
  // Imprimir minuta. A lista nao diz se ha boleto: o dialogo le as parcelas e desliga
  // o download sem boleto anexado, com o motivo; a cotacao emitida abre os boletos da
  // apolice. A minuta fica desligada, com o motivo, ate a aprovacao da seguradora.
  const acoesDaLinha = (operacao: Operation): AcaoDoMenu[] => {
    const lista: AcaoDoMenu[] = [
      {
        rotulo: 'Visualizar cotacao',
        onSelect: () => void navigate(`/operacoes/${operacao.id}`),
      },
    ];
    if (podeCancelar && podeCancelarNaLista(operacao.stage, escopo)) {
      lista.push({
        rotulo: 'Cancelar cotacao',
        perigo: true,
        onSelect: () => setCancelando(operacao),
      });
    }
    lista.push(
      { rotulo: 'Imprimir cotacao', onSelect: () => imprimir.mutate(operacao) },
      { rotulo: 'Baixar boleto(s)', onSelect: () => setBoletosDe(operacao) },
    );
    if (podeImprimirMinuta) {
      lista.push({
        rotulo: 'Imprimir minuta',
        motivo: motivoSemMinuta(operacao),
        onSelect: () =>
          imprimirMinuta.mutate({ id: operacao.id, numero: numeroDe(operacao) }),
      });
    }
    return lista;
  };
  const ocupada = (operacao: Operation) =>
    (imprimir.isPending && imprimir.variables?.id === operacao.id) ||
    (imprimirMinuta.isPending && imprimirMinuta.variables?.id === operacao.id);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Cotacoes"
        description="As cotacoes da sua corretora, da cotacao a emissao."
        actions={
          podeCriar ? (
            <Button onClick={() => setEscolhendo(true)}>
              <Icon name="sparkle" size={15} />
              Nova cotacao ou endosso
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as cotacoes">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por numero, objeto ou tomador"
          extra={
            <Button
              size="sm"
              variant="secondary"
              className="self-end"
              onClick={() => setFiltrando(true)}
            >
              {aplicados > 0 ? `Filtros (${aplicados})` : 'Filtros'}
            </Button>
          }
        />

        <ListaDaCorretora
          items={consulta.data?.items ?? []}
          loading={consulta.isPending}
          onAbrir={(operacao) => void navigate(`/operacoes/${operacao.id}`)}
          acoes={{ acoes: acoesDaLinha, ocupada }}
          empty={
            <EmptyState
              icon="clock"
              title={filtrado ? 'Nenhuma cotacao encontrada' : 'Nenhuma cotacao ainda'}
              description={
                filtrado
                  ? 'Ajuste os filtros para ver outras cotacoes.'
                  : 'A cotacao nasce no assistente e fica com a corretora ate o envio para analise.'
              }
              action={
                podeCriar && !filtrado ? (
                  <Button onClick={() => setEscolhendo(true)}>Nova cotacao</Button>
                ) : null
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <BrokerQuoteFiltersDialog
        open={filtrando}
        value={filtros}
        onClose={() => setFiltrando(false)}
        onApply={(novos) => {
          setFiltros(novos);
          setOffset(0);
          setFiltrando(false);
        }}
      />

      {podeCriar ? (
        <QuoteTypeDialog open={escolhendo} onClose={() => setEscolhendo(false)} />
      ) : null}

      {cancelando ? (
        <QuoteWithdrawDialog
          operationId={cancelando.id}
          open
          onClose={() => setCancelando(null)}
        />
      ) : null}

      {boletosDe ? (
        <BoletosDaCotacao
          operationId={boletosDe.id}
          numero={numeroDe(boletosDe)}
          emitida={boletosDe.stage === 'emitida'}
          open
          onClose={() => setBoletosDe(null)}
        />
      ) : null}
    </div>
  );
}
